import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Tooltip,
  Alert
} from '@mui/material';
import {
  FilterList as FilterIcon,
  MonetizationOn as MoneyIcon,
  CheckCircle as CheckIcon,
  Block as BlockIcon,
  Savings as SavingsIcon
} from '@mui/icons-material';

interface FAQFilteringStatsProps {
  stats?: {
    last_run: string;
    total_questions: number;
    venue_specific: number;
    general_tourism: number;
    percentage_filtered: number;
    estimated_cost_savings: string;
  };
}

export const FAQFilteringStats: React.FC<FAQFilteringStatsProps> = ({ stats }) => {
  if (!stats) return null;

  const savingsAmount = parseFloat(stats.estimated_cost_savings);
  const savingsColor = savingsAmount > 0.5 ? 'success' : savingsAmount > 0.1 ? 'warning' : 'info';

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #f5f5f5 0%, #e8f5e9 100%)' }}>
      <Box display="flex" alignItems="center" mb={2}>
        <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h3">
          FAQ Filtering Cost Savings
        </Typography>
        <Chip 
          label={`${stats.percentage_filtered}% Filtered`}
          color={savingsAmount > 0.5 ? 'success' : 'warning'}
          size="small"
          sx={{ ml: 'auto' }}
        />
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Question Distribution
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" alignItems="center">
                  <CheckIcon sx={{ fontSize: 16, mr: 0.5, color: 'success.main' }} />
                  <Typography variant="body2">
                    Venue-Specific (Processed)
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="bold">
                  {stats.venue_specific} questions
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center">
                  <BlockIcon sx={{ fontSize: 16, mr: 0.5, color: 'error.main' }} />
                  <Typography variant="body2">
                    General Tourism (Filtered)
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="bold">
                  {stats.general_tourism} questions
                </Typography>
              </Box>
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={stats.percentage_filtered} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: 'success.light',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'error.main'
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {stats.total_questions} total questions analyzed
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Cost Impact
            </Typography>
            <Paper elevation={0} sx={{ p: 2, backgroundColor: `${savingsColor}.lighter`, border: `1px solid`, borderColor: `${savingsColor}.light` }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <SavingsIcon sx={{ mr: 1, color: `${savingsColor}.main` }} />
                  <Typography variant="h5" color={`${savingsColor}.dark`}>
                    ${savingsAmount.toFixed(2)}
                  </Typography>
                </Box>
                <Tooltip title="Estimated savings based on Claude API pricing ($0.003 per question)">
                  <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                    saved on this schema
                  </Typography>
                </Tooltip>
              </Box>
            </Paper>
            
            <Alert severity="info" sx={{ mt: 2 }} icon={<MoneyIcon />}>
              <Typography variant="caption">
                By filtering out {stats.general_tourism} general tourism questions, 
                we reduced API costs by {stats.percentage_filtered}% while maintaining 
                venue-specific FAQ quality.
              </Typography>
            </Alert>
          </Box>
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'right' }}>
        Last filtered: {new Date(stats.last_run).toLocaleString()}
      </Typography>
    </Paper>
  );
};